<template>
  <ViewerLayoutRoot class="video-guest-layout" data-testid="video-guest-layout">
    <GuestTopBar compact-desktop-top-spacing />

    <!-- Summary is its own focused guest layout. Keeping it out of the split
         view lets its content determine page height instead of capping it to
         the adjacent video's aspect ratio. -->
    <div v-if="isSummaryLayout" class="video-guest-layout__summary">
      <div v-if="isSmallMobile" class="mb-3 px-4 sm:px-0">
        <ItemName />
        <ViewerModeToggle v-if="availableModes.length > 1" class="video-guest-layout__mobile-mode-toggle" />
      </div>
      <div class="px-4 sm:px-0">
        <SummarySidePanel />
      </div>
      <div class="video-guest-layout__video mt-4" data-testid="mobile-summary-video">
        <ViewerContent />
      </div>
    </div>

    <template v-else>
      <!-- Split view: Logs mode or desktop Video mode with chapters -->
      <template v-if="splitConfig">
        <SplitViewContainer
          :key="splitConfig.storageKey"
          class="video-guest-layout__split"
          :class="{ 'video-guest-layout__split--logs': canUseLogs && isLogsMode }"
          :full-width="isTheaterModeEnabled"
          v-bind="splitConfig"
        >
          <template #left>
            <div class="video-guest-layout__video-col">
              <div class="video-guest-layout__video">
                <ViewerContent :class="{ 'viewer-video-theater-frame': isTheaterModeEnabled }" />
              </div>
              <MetadataSection :show-toolbar="false" />
            </div>
          </template>
          <template #right>
            <div class="video-guest-layout__side-panel">
              <component :is="rightPanel" />
            </div>
          </template>
        </SplitViewContainer>
      </template>

      <!-- Stacked: mobile or no chapters -->
      <div
        v-else
        class="video-guest-layout__media-row"
        :class="{
          'video-guest-layout__media-row--single': !rightPanel || isTheaterModeEnabled,
          'video-guest-layout__media-row--summary': canUseSummary && isSummaryMode,
        }"
      >
        <div class="video-guest-layout__video-col">
          <div class="video-guest-layout__video">
            <ViewerContent :class="{ 'viewer-video-theater-frame': isTheaterModeEnabled }" />
          </div>
          <div v-if="isSmallMobile" class="mt-3 px-4">
            <ItemName />
            <ViewerModeToggle v-if="availableModes.length > 1" class="video-guest-layout__mobile-mode-toggle" />
          </div>
          <div class="px-4 sm:px-0">
            <MetadataSection :show-toolbar="false" />
          </div>
        </div>
        <div v-if="rightPanel && !isTheaterModeEnabled" class="video-guest-layout__side-panel px-4 sm:px-0">
          <component :is="rightPanel" />
        </div>
      </div>

      <EngagementBar />

      <div class="video-guest-layout__tabs px-4 sm:px-0">
        <VideoGuestTabs
          :hide-summary="isSummaryMode && isDesktop"
          :show-theater-chapters="isTheaterModeEnabled && hasChapters"
          @addedComment="emit('addedComment')"
        />
      </div>
    </template>
  </ViewerLayoutRoot>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch, watchEffect, type Component } from 'vue';
import { storeToRefs } from 'pinia';

import ViewerContent from '@/modules/viewer/components/Content.vue';
import ItemName from '@/modules/viewer/components/Name.vue';
import ViewerModeToggle from '@/modules/viewer/components/ViewerModeToggle.vue';
import GuestTopBar from '../../shared/GuestTopBar.vue';
import EngagementBar from '../../shared/EngagementBar.vue';
import MetadataSection from '../../shared/MetadataSection.vue';
import GuestChaptersSidePanel from './GuestChaptersSidePanel.vue';
import VideoGuestTabs from './VideoGuestTabs.vue';
import LogsSidePanel from '@/modules/viewer/components/logs/LogsSidePanel.vue';
import LogsMobileNotice from '@/modules/viewer/components/logs/LogsMobileNotice.vue';
import SummarySidePanel from '@/modules/viewer/components/SummarySidePanel.vue';
import SplitViewContainer from '@/modules/viewer/components/SplitViewContainer.vue';

import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useViewerModeStore, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { useViewerTheaterMode } from '@/modules/viewer/composables/useViewerTheaterMode';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';
import { useViewerModeUrlSync } from '@/modules/viewer/composables/useViewerModeUrlSync';
import { useMobileDetection } from '@/composables/useMobileDetection';

import ViewerLayoutRoot from '../../shared/ViewerLayoutRoot.vue';
import { VIDEO_RIGHT_PANE_SPLIT_CONFIG } from '../../shared/videoSplitConfig';
const emit = defineEmits<{
  addedComment: [];
}>();

const { hasChapters } = storeToRefs(useAiMetadataStore());

const viewerModeStore = useViewerModeStore();
const { theaterModeStore, isTheaterModeEnabled } = useViewerTheaterMode();
const layoutStore = useLayoutStore();
const { isLogsMode, isSummaryMode, currentMode } = storeToRefs(viewerModeStore);

const { availableModes, isMobile } = useViewerAvailableModes({ syncCurrentMode: true });
const canUseLogs = computed(() => availableModes.value.includes(ViewerMode.LOGS));
const canUseSummary = computed(() => availableModes.value.includes(ViewerMode.SUMMARY));
// Mobile uses a focused, natural-flow Summary layout. Desktop keeps the
// existing video + SummarySidePanel grid further below.
const isSummaryLayout = computed(() => canUseSummary.value && isSummaryMode.value && isMobile.value);
const { isMobile: isSmallMobile } = useMobileDetection();
const isDesktop = computed(() => !isMobile.value);
useViewerModeUrlSync();

watch(
  isTheaterModeEnabled,
  isEnabled => {
    layoutStore.setTheaterSidebarCollapsed(isEnabled);
  },
  { immediate: true }
);

// Summary mode never uses the split view: its content is often much taller
// than the video's aspect-ratio height, which the split (whichever slot
// drives it) would otherwise cap it to — squeezing it into a tiny scrollable
// box with a large empty gap below. It falls through to the natural-flow
// grid further below instead, where both columns size to their own content.
const splitConfig = computed(() => {
  if (canUseLogs.value && isLogsMode.value && isDesktop.value) {
    return { defaultRatio: 0.4, minRatio: 0.25, maxRatio: 0.6, storageKey: 'viewer-logs-split-ratio', reversed: true };
  }
  if (isDesktop.value && hasChapters.value && !isSummaryMode.value) {
    return VIDEO_RIGHT_PANE_SPLIT_CONFIG;
  }
  return null;
});

const rightPanel = computed<Component | null>(() => {
  if (canUseLogs.value && isLogsMode.value) return isDesktop.value ? LogsSidePanel : LogsMobileNotice;
  if (canUseSummary.value && isSummaryMode.value) return SummarySidePanel;
  return hasChapters.value ? GuestChaptersSidePanel : null;
});

// Defensive guard: if a viewer who can't use Logs somehow lands in LOGS mode
// (e.g. shared URL with ?mode=logs), snap them back to Video.
watchEffect(() => {
  if (!canUseLogs.value && currentMode.value === ViewerMode.LOGS) {
    viewerModeStore.setMode(ViewerMode.VIDEO);
  }
});

onUnmounted(() => {
  layoutStore.setTheaterSidebarCollapsed(false);
  theaterModeStore.disableTheaterMode();
});
</script>

<style scoped>
.video-guest-layout__media-row {
  @apply flex flex-col gap-4;
}

@media (min-width: 1024px) {
  .video-guest-layout__media-row {
    display: grid;
    grid-template-columns: 1fr minmax(280px, 360px);
    align-items: stretch;
    gap: 1rem;
  }

  .video-guest-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 1440px) {
  .video-guest-layout__media-row {
    grid-template-columns: 1fr 360px;
  }

  .video-guest-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

.video-guest-layout__video-col {
  @apply flex flex-col min-w-0;
}

/* Summary mode: Summary is the primary content — large, on the left; video
   is secondary — smaller, on the right. Visual-only reorder (like
   SplitViewContainer's `reversed` prop) so the grid's column-size tracks
   stay unchanged; only which element fills each track flips. */
@media (min-width: 1024px) {
  .video-guest-layout__media-row--summary .video-guest-layout__video-col {
    order: 2;
  }

  .video-guest-layout__media-row--summary .video-guest-layout__side-panel {
    order: 1;
  }
}

@media (min-width: 1800px) {
  .video-guest-layout__media-row--summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.video-guest-layout__mobile-mode-toggle {
  @apply mt-2 -ml-3;
}

.video-guest-layout__video {
  @apply min-w-0;
}

.video-guest-layout__video :deep(.content-wrapper) {
  background-color: transparent !important;
}

.video-guest-layout__side-panel {
  @apply flex flex-col w-full mt-2;
}

@media (min-width: 1024px) {
  .video-guest-layout__side-panel {
    @apply mt-0;
  }

  .video-guest-layout__side-panel :deep(.guest-chapters-panel) {
    @apply flex-grow;
    max-height: 100%;
    overflow-y: auto;
  }
}

.video-guest-layout__tabs {
  @apply mt-2;
}

/* Split view (Logs mode and Video mode with chapters) */
.video-guest-layout__split {
  min-height: 0;
}

.video-guest-layout__split :deep(.video-guest-layout__side-panel) {
  @apply mt-0 h-full;
}

.video-guest-layout__split :deep(.logs-panel) {
  @apply h-full;
}

.video-guest-layout__split :deep(.video-guest-layout__video-col) {
  @apply h-full;
}
</style>
